import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Coffee, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button/button";
import { getTodayBreakStatusQueryOptions } from "@/config/queries/emp-query-options";

import { useBreakActions } from "./use-break-actions";
import { StartBreakDialog } from "./start-break-dialog";

/**
 * Employee-only navbar control. Three states: not checked in (disabled),
 * working ("Take a break" → reason dialog), on a break (amber "Resume").
 */
export const BreakControl = () => {
  const key = Cookies.get("key") as string;
  const { data } = useQuery(getTodayBreakStatusQueryOptions(key));
  const { startBreak, endBreak, isStarting, isEnding } = useBreakActions();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!data) return null;

  if (data.onBreak) {
    return (
      <Button
        size="sm"
        onClick={() => endBreak()}
        disabled={isEnding}
        className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
        data-testid="break-resume-button"
      >
        {isEnding ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black/60 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
          </span>
        )}
        Resume
      </Button>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setDialogOpen(true)}
        disabled={!data.checkedIn}
        title={data.checkedIn ? "Take a break" : "Check in first"}
        className="gap-2"
        data-testid="break-start-button"
      >
        <Coffee className="w-4 h-4" />
        <span className="hidden sm:inline">Take a break</span>
      </Button>
      <StartBreakDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        isPending={isStarting}
        onStart={(reason) =>
          startBreak({ reason }, { onSuccess: () => setDialogOpen(false) })
        }
      />
    </>
  );
};
